/**
 * Whether the Dex page's Live Scan menu is open, held OUTSIDE the page.
 *
 * 2026-09-03 device report (Galaxy S21+): a noticeable pause between tapping
 * Live Scan and the sheet appearing, long enough to tap again and land in the
 * "How to scan" guide when it finally came up. The open flag was React state
 * on the Pokédex page itself, so every open and close re-rendered the whole
 * page: the grid hands each PokedexCell fresh inline callbacks, which defeats
 * its memo, so that meant reconciling every cell of the dex before the sheet
 * could paint. The flag now lives in this tiny store; the page renders the
 * menu through a small host component that subscribes here, and opening the
 * menu re-renders the host alone.
 */
import { useSyncExternalStore } from "react";

let open = false;
const listeners = new Set<() => void>();
/** When the menu was last asked to open (Date.now()), for the open-latency log. */
let requestedAt = 0;

/**
 * Window events the global menu host fires so the Pokédex page can refresh
 * its totals without hosting the menu itself (2026-09-23: the menu used to be
 * rendered INSIDE the Pokédex page, so a Live Scan tap from any other screen
 * waited for that whole page to mount before the sheet could paint, ~10 s on
 * a Galaxy S21+).
 */
export const DEX_SCAN_MENU_CLOSED_EVENT = "dex:scan-menu-closed";
export const DEX_SCAN_MENU_CONFIRMED_EVENT = "dex:scan-menu-confirmed";

export function dexLiveScanMenuRequestedAt(): number {
  return requestedAt;
}

function set(next: boolean): void {
  if (open === next) return;
  open = next;
  for (const l of listeners) l();
}

export function openDexLiveScanMenu(): void {
  if (!open) requestedAt = Date.now();
  set(true);
}

export function closeDexLiveScanMenu(): void {
  set(false);
}

export function isDexLiveScanMenuOpen(): boolean {
  return open;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useDexLiveScanMenuOpen(): boolean {
  return useSyncExternalStore(subscribe, isDexLiveScanMenuOpen, () => false);
}

/** Test seam. */
export function _resetDexLiveScanMenuForTests(): void {
  open = false;
}
